import type { Book, FolderRef, Track } from '../types';
import { isAudioFile, isImageFile, naturalCompare, prettyTrackName, stripExtension } from '../lib/format';
import { driveStreamUrl } from '../lib/swBridge';
import { FOLDER_MIME, listChildren, type DriveFile } from './client';

/**
 * Turns a Drive folder tree into a library.
 *
 * The rules are intentionally shape-driven rather than convention-driven, so
 * that no particular naming scheme is required:
 *
 *   - a folder that directly contains audio files IS a book;
 *   - an audio file sitting loose in the root is a single-track book;
 *   - subfolders are scanned too, so /Author/Title/*.mp3 works as well as
 *     /Title/*.mp3 or a flat pile of .m4b files.
 *
 * Every book also records the folders above it (`folderPath`), so the library
 * can show "Top 100 books" as one tile instead of a hundred loose books.
 */

/** Guards against a pathological tree (or a symlink-ish loop) eating the quota. */
const MAX_DEPTH = 6;
const CONCURRENCY = 4;

export interface ScanResult {
  books: Book[];
  foldersScanned: number;
}

export async function scanLibrary(
  rootFolderId: string,
  onProgress?: (foldersScanned: number, booksFound: number) => void,
): Promise<ScanResult> {
  const books: Book[] = [];
  const seen = new Set<string>();
  let foldersScanned = 0;

  interface QueueItem {
    id: string;
    name: string;
    parentName: string | null;
    depth: number;
    /** Grouping folders above this one, excluding the root. */
    path: FolderRef[];
  }

  let queue: QueueItem[] = [
    { id: rootFolderId, name: '', parentName: null, depth: 0, path: [] },
  ];

  while (queue.length > 0) {
    const batch = queue.splice(0, CONCURRENCY);
    const next: QueueItem[] = [];

    const results = await Promise.all(
      batch.map(async (item) => ({ item, children: await listChildren(item.id) })),
    );

    for (const { item, children } of results) {
      foldersScanned += 1;
      const isRoot = item.depth === 0;

      const folders = children.filter((f) => f.mimeType === FOLDER_MIME);
      const audio = children.filter((f) => f.mimeType !== FOLDER_MIME && isAudioFile(f.name, f.mimeType));
      const images = children.filter((f) => isImageFile(f.name, f.mimeType));

      if (audio.length > 0) {
        if (isRoot) {
          // Loose files at the top level are each their own single-file book.
          for (const file of audio) {
            if (seen.has(file.id)) continue;
            seen.add(file.id);
            books.push(singleFileBook(file, imageNamedLike(images, file)));
          }
        } else if (!seen.has(item.id)) {
          seen.add(item.id);
          books.push(folderBook(item.id, item.name, item.parentName, item.path, audio, images));
        }
      }

      // A folder becomes part of its children's path only when it is a pure
      // grouping folder. A book folder with a subfolder (say, "Extras") keeps
      // that subfolder beside it, rather than turning the book into a folder.
      const childPath =
        isRoot || audio.length > 0 ? item.path : [...item.path, { id: item.id, name: item.name }];

      if (item.depth < MAX_DEPTH) {
        for (const folder of folders) {
          next.push({
            id: folder.id,
            name: folder.name,
            parentName: isRoot ? null : item.name,
            depth: item.depth + 1,
            path: childPath,
          });
        }
      }
    }

    queue = next.concat(queue);
    onProgress?.(foldersScanned, books.length);
  }

  books.sort((a, b) => naturalCompare(a.title, b.title));
  return { books, foldersScanned };
}

function toTrack(file: DriveFile): Track {
  return {
    id: file.id,
    name: prettyTrackName(file.name),
    fileName: file.name,
    mimeType: file.mimeType,
    sizeBytes: Number(file.size) || 0,
  };
}

function folderBook(
  id: string,
  name: string,
  parentName: string | null,
  path: FolderRef[],
  audio: DriveFile[],
  images: DriveFile[],
): Book {
  const ordered = [...audio].sort((a, b) => naturalCompare(a.name, b.name));
  const tracks = ordered.map(toTrack);
  const cover = pickCover(images);

  return {
    id,
    title: name.trim() || 'Untitled',
    ...(parentName ? { author: parentName.trim() } : {}),
    ...(cover ? { coverUrl: cover } : {}),
    kind: 'folder',
    tracks,
    totalSizeBytes: tracks.reduce((sum, t) => sum + t.sizeBytes, 0),
    ...(path.length > 0 ? { folderPath: path } : {}),
  };
}

function singleFileBook(file: DriveFile, cover: string | undefined): Book {
  const track = toTrack(file);
  // A lone file has no folder to take a title from, so use the filename.
  track.name = stripExtension(file.name);
  return {
    id: file.id,
    title: stripExtension(file.name),
    ...(cover ? { coverUrl: cover } : {}),
    kind: 'single',
    tracks: [track],
    totalSizeBytes: track.sizeBytes,
  };
}

/**
 * Cover images are served through the same service-worker proxy as the audio,
 * which means an authenticated Drive image works in a plain <img src> with no
 * extra plumbing.
 *
 * A book with no image beside it gets the art embedded in its audio instead
 * (store/covers.ts). Drive's `thumbnailLink` is deliberately not used: Drive
 * rarely has one for audio, and the link expires within hours while the
 * library is cached for much longer, which left broken images behind.
 */
function pickCover(images: DriveFile[]): string | undefined {
  const preferred = images.find((f) => /^(cover|folder|front|art)\b/i.test(f.name));
  const chosen = preferred ?? images[0];
  return chosen ? driveStreamUrl(chosen.id) : undefined;
}

/** Loose files at the root share one folder, so a picture there belongs to a
 *  book only when it is named after it: "Dune.jpg" for "Dune.m4b". */
function imageNamedLike(images: DriveFile[], file: DriveFile): string | undefined {
  const name = stripExtension(file.name).trim().toLowerCase();
  const match = images.find((f) => stripExtension(f.name).trim().toLowerCase() === name);
  return match ? driveStreamUrl(match.id) : undefined;
}
